/**
 * Portal Manta · OrcamentoRevisaoTab
 * Extraído de App.jsx (v29 modular)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { OBRAS_ROD, S, V, extKm, fmtBRL, fmtNum, sigKey, sumBy } from '../../globals.js';
import { getQtdObra } from '../agentes/getQtdObra.jsx';
import { CONSOL_FIN } from '../../data/consol-fin.js';
import { SICRO_DESC } from '../../data/sicro-desc.js';
import { executarPipeline } from '../agentes/executarPipeline.jsx';
import { AjusteInput } from '../charts/AjusteInput.jsx';
import { M } from '../../tokens/colors.js';
import { THl, TH, TDl, TD } from '../../app-globals.js';

const OrcamentoRevisaoTab = memo(function OrcamentoRevisaoTab_Inner({lote}){
  const sig = sigKey(lote);
  const SK = 'manta-orcparam-revisao-L' + sig;
  const od = OBRAS_ROD[sig] || {};
  const ext = extKm(lote);

  const _load = (key, def) => {
    if (typeof window === 'undefined') return def;
    try {
      const saved = JSON.parse(window.localStorage.getItem(SK) || '{}');
      return saved[key] != null ? saved[key] : def;
    } catch { return def; }
  };
  const _initIndiretos = {
    ADM_LOCAL:   {meses:0, valorMes:0},
    CANTEIRO:    {meses:0, valorMes:0},
    MOBILIZACAO: {meses:0, valorMes:0},
    EQUIPE:      {meses:0, valorMes:0},
  };
  const [indiretos, setIndiretos] = useState(()=>{
    const v = _load('indiretos', null);
    if (v && typeof v === 'object') return {..._initIndiretos, ...v};
    const old = _load('bases', null);
    if (old && typeof old.ADM_LOCAL === 'number' && old.ADM_LOCAL > 0) return {..._initIndiretos, ADM_LOCAL:{meses:1, valorMes:old.ADM_LOCAL}};
    if (typeof _load('admLocal', null) === 'number') return {..._initIndiretos, ADM_LOCAL:{meses:1, valorMes:_load('admLocal', 0)}};
    return _initIndiretos;
  });
  const [ajustes, setAjustes] = useState(()=> _load('ajustes', {}));
  // NOVO: overrides Q e CU das obras 1.2.x
  const [obrasOv, setObrasOv] = useState(()=> _load('obrasOv', {}));
  const [bdiComp, setBdiComp] = useState(()=> _load('bdiComp', {AC:3.50, DF:1.00, R:1.00, CT:0.50, SG:0.50, L:8.00, PIS:0.65, COFINS:3.00, ISS:2.00, CPRB:0.00})); // v28: default = preset MEF ANTT (~23%)
  const [bdiMeses, setBdiMeses] = useState(()=> _load('bdiMeses', {DF:0, SG:0}));
  const [abcServOv, setAbcServOv] = useState(()=> _load('abcServOv', {}));
  const [abcInsLista, setAbcInsLista] = useState(()=> _load('abcInsLista', []));

  useEffect(()=>{
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(SK, JSON.stringify({indiretos, ajustes, obrasOv, bdiComp, bdiMeses, abcServOv, abcInsLista, _v:6})); } catch {}
  }, [SK, indiretos, ajustes, obrasOv, bdiComp, bdiMeses, abcServOv, abcInsLista]);

  const calcRevisado = (base, aj) => {
    if (!aj || !aj.valor) return base;
    if (aj.tipo === '%') return base * (1 + aj.valor/100);
    return base + aj.valor;
  };

  // ── OBRAS 1.2.x com Q e CU editáveis ─────────────────────────
  const linhasObras = useMemo(()=>{
    const itens = lote.dados_manta?.compara_parametrico?.itens || [];
    return itens.filter(it => {
      const cod = (it.codigo||'').trim().replace(/\.$/,'');
      const partes = cod.split('.');
      return partes.length===3 && partes[0]==='1' && partes[1]==='2' && (it.descricao||'').toUpperCase().trim() !== 'BDI';
    }).map(it => {
      const cod = (it.codigo||'').trim().replace(/\.$/,'');
      const editalTotal = it.pr_vias?.preco_total || 0;
      const qOrig = getQtdObra(cod, od);
      const cuOrig = (qOrig.qtd && qOrig.qtd > 0 && editalTotal) ? editalTotal / qOrig.qtd : null;
      const ov = obrasOv[cod] || {};
      const qRev = ov.qtd != null ? ov.qtd : qOrig.qtd;
      const cuRev = ov.cu != null ? ov.cu : cuOrig;
      const undRev = ov.und != null ? ov.und : qOrig.und;
      const temQCU = (qRev != null && qRev > 0) && (cuRev != null && cuRev > 0);
      const totalBase = temQCU ? qRev * cuRev : editalTotal;
      const aj = ajustes[cod] || {tipo:'%',valor:0};
      const totalRev = calcRevisado(totalBase, aj);
      const ovTouched = ov.qtd != null || ov.cu != null || ov.und != null;
      const ajTouched = aj.valor !== 0;
      return {
        key: cod, cod, desc: it.descricao,
        qOrig: qOrig.qtd, undOrig: qOrig.und, cuOrig,
        qRev, cuRev, undRev,
        editalTotal, totalBase, totalRev,
        aj, ovTouched, ajTouched,
        touched: ovTouched || ajTouched,
      };
    });
  }, [sig, ajustes, obrasOv]);
  const subtotalObrasEdital = sumBy(linhasObras, 'editalTotal');
  const subtotalObrasRev = sumBy(linhasObras, 'totalRev');

  const indMeta = [
    {key:'ADM_LOCAL',   desc:'ADM Local'},
    {key:'CANTEIRO',    desc:'Canteiro'},
    {key:'MOBILIZACAO', desc:'Mobilização'},
    {key:'EQUIPE',      desc:'Equipe'},
  ];
  const linhasIndiretos = indMeta.map(m => {
    const v = indiretos[m.key] || {meses:0, valorMes:0};
    const base = (v.meses||0) * (v.valorMes||0);
    const aj = ajustes[m.key] || {tipo:'%',valor:0};
    return {key:m.key, desc:m.desc, meses:v.meses||0, valorMes:v.valorMes||0, base, aj, revisado:calcRevisado(base,aj)};
  });
  const subtotalIndBase = sumBy(linhasIndiretos, 'base');
  const subtotalIndRev = sumBy(linhasIndiretos, 'revisado');

  // ── Q3 TOP-10 SERVIÇOS SICRO (com acumulado) ─────────────────
  const top10ABCServ = useMemo(()=>{
    const cf = CONSOL_FIN[sig] || {};
    const all = Object.entries(cf).flatMap(([d,items]) => items.map(it => ({...it, _d:d})));
    all.sort((a,b)=> (b.v||0) - (a.v||0));
    const tot = sumBy(all, 'v') || 1;
    let acum = 0;
    const classeA = [];
    for (const it of all) {
      acum += (it.v||0);
      if (acum/tot*100 <= 80) classeA.push(it); else break;
    }
    const top = classeA.slice(0, 10);
    let acumRev = 0, acumBase = 0;
    return top.map((it) => {
      const cuOrig = (it.p || 0) * (1 + (it.b || 0));
      const ov = abcServOv[it.c] || {};
      const qtdRev = ov.qtd != null ? ov.qtd : (it.q || 0);
      const cuRev  = ov.cu  != null ? ov.cu  : cuOrig;
      const totalRev = qtdRev * cuRev;
      const totalBase = it.v || 0;
      acumRev += totalRev; acumBase += totalBase;
      return {
        c: it.c, desc: SICRO_DESC[it.c] || '(sem descrição)',
        qtd: it.q || 0, und: it.u || '—', cu: cuOrig, total: totalBase,
        qtdRev, cuRev, totalRev,
        acumBase, acumRev, pctAcumRev: tot ? acumRev/tot*100 : 0,
      };
    });
  }, [sig, abcServOv]);
  const top10ServBase = sumBy(top10ABCServ, 'total');
  const top10ServRev = sumBy(top10ABCServ, 'totalRev');
  const deltaQ3 = top10ServRev - top10ServBase;
  const q3Touched = Object.values(abcServOv).some(o => (o?.qtd != null) || (o?.cu != null));

  // ── Q4 TOP-10 INSUMOS (sem placeholders) ─────────────────────
  const insumosCalc = abcInsLista.map(it => ({...it, total: (it.qtd||0) * (it.cu||0)})).sort((a,b)=> b.total - a.total);
  const totalInsTodos = sumBy(insumosCalc, 'total') || 0;
  const top10ABCIns = useMemo(()=>{
    let acum = 0;
    return insumosCalc.slice(0, 10).map((it) => {
      acum += it.total;
      return {...it, acum, pctAcum: totalInsTodos ? acum/totalInsTodos*100 : 0};
    });
  }, [abcInsLista]);
  const top10InsBase = sumBy(top10ABCIns, 'total');
  const deltaQ4 = totalInsTodos; // total dos insumos é o delta (começam vazios)
  const q4Touched = abcInsLista.length > 0 && totalInsTodos > 0;

  // ── DETECÇÃO DE DUPLA CONTAGEM ───────────────────────────────
  const obrasTouched = linhasObras.some(l => l.touched);
  const duplaContagem = obrasTouched && (q3Touched || q4Touched);

  // ── CUSTO DIRETO ─────────────────────────────────────────────
  const custoDiretoEdital = subtotalObrasEdital + subtotalIndBase;
  const custoDiretoManta = subtotalObrasRev + subtotalIndRev + deltaQ3 + deltaQ4;

  // ── BDI ──────────────────────────────────────────────────────
  const bdiCalculado = useMemo(()=>{
    const c = bdiComp;
    const T = ((c.PIS||0) + (c.COFINS||0) + (c.ISS||0) + (c.CPRB||0)) / 100;
    const AC = (c.AC||0)/100, SG = (c.SG||0)/100, R = (c.R||0)/100, CT = (c.CT||0)/100;
    const DF = (c.DF||0)/100, L = (c.L||0)/100;
    if (T >= 1) return null;
    return ((1+AC+CT+SG+R) * (1+DF) * (1+L) / (1-T)) - 1;
  }, [bdiComp]);
  const bdiFator = bdiCalculado != null ? (1 + bdiCalculado) : 1;
  const totalGeralEdital = custoDiretoEdital * bdiFator;
  const totalGeralManta = custoDiretoManta * bdiFator;
  // v28 · Estado para auto-decomposição com aviso
  const [bdiAutoAviso, setBdiAutoAviso] = useState(false); // true quando aplicado preset proporcional ao MEF
  const [bdiMefRef, setBdiMefRef] = useState(null); // BDI agregado importado do MEF (referência readonly)

  const presetsBDI = {
    'DNIT 2022 Rodovia':    {AC:4.00, DF:1.23, R:1.27, CT:0,    SG:0.25, L:8.96, PIS:0.65, COFINS:3.00, ISS:2.00, CPRB:0.00},
    'MEF ANTT (~23%)':      {AC:3.50, DF:1.00, R:1.00, CT:0.50, SG:0.50, L:8.00, PIS:0.65, COFINS:3.00, ISS:2.00, CPRB:0.00},
    'Zerar componentes':    {AC:0,DF:0,R:0,CT:0,SG:0,L:0,PIS:0,COFINS:0,ISS:0,CPRB:0},
  };

  // ── v28 · BDI Auto-decomposição proporcional ao MEF ────────────────
  // Pega o BDI agregado oficial do MEF e distribui proporcionalmente sobre o preset MEF ANTT
  const aplicarBdiDoMef = (bdiOficialPct) => {
    // bdiOficialPct = 22.69 (do MEF construcao_rodoviaria, por exemplo)
    if (!bdiOficialPct || bdiOficialPct <= 0) return;
    const presetBase = presetsBDI['MEF ANTT (~23%)'];
    // Calcular BDI nominal do preset base (~22.97% pelo formula TCU)
    const tBase = (presetBase.PIS + presetBase.COFINS + presetBase.ISS + presetBase.CPRB) / 100;
    const bdiBase = (1 + (presetBase.AC + presetBase.CT + presetBase.SG + presetBase.R)/100)
                  * (1 + presetBase.DF/100)
                  * (1 + presetBase.L/100)
                  / (1 - tBase) - 1;
    // Fator de proporcionalidade
    const fator = (bdiOficialPct/100) / bdiBase;
    // Aplicar fator nos componentes diretos (deixa tributos fixos pois são alíquotas legais)
    const ajustado = {
      AC:     +(presetBase.AC * fator).toFixed(2),
      DF:     +(presetBase.DF * fator).toFixed(2),
      R:      +(presetBase.R * fator).toFixed(2),
      CT:     +(presetBase.CT * fator).toFixed(2),
      SG:     +(presetBase.SG * fator).toFixed(2),
      L:      +(presetBase.L * fator).toFixed(2),
      PIS:    presetBase.PIS,
      COFINS: presetBase.COFINS,
      ISS:    presetBase.ISS,
      CPRB:   presetBase.CPRB,
    };
    setBdiComp(ajustado);
    setBdiMefRef(bdiOficialPct);
    setBdiAutoAviso(true);
  };

  // Tenta detectar BDI do MEF na importação JSON (efeito colateral)
  const detectarBdiDoMef = (json) => {
    // formatos suportados: {bdi: {construcao_rodoviaria: 0.2269}} OU {mef_combinado: {bdi: ...}}
    const bdi = json?.bdi || json?.mef_combinado?.bdi;
    if (!bdi) return null;
    // Prioridade: construção rodoviária (Lote 1 = 0.2269)
    const v = bdi.construcao_rodoviaria || bdi.restauracao_rodoviaria || null;
    if (typeof v === 'number' && v > 0) return v * 100; // em %
    return null;
  };

  // ── EXPORTAR / IMPORTAR / RESETAR ────────────────────────────
  const exportar = () => {
    if (typeof window === 'undefined') return;
    const payload = {lote:sig, lote_nome:lote.nome, indiretos, ajustes, obrasOv, bdiComp, bdiMeses, bdiCalculado, abcServOv, abcInsLista, custoDiretoEdital, custoDiretoManta, totalGeralEdital, totalGeralManta, deltaQ3, deltaQ4, exportado_em:new Date().toISOString(), _v:6};
    const blob = new Blob([JSON.stringify(payload, null, 2)], {type:'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `manta-revisao-L${sig}-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const importar = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data.indiretos) setIndiretos({..._initIndiretos, ...data.indiretos});
        else if (data.bases?.ADM_LOCAL != null) setIndiretos({..._initIndiretos, ADM_LOCAL:{meses:1, valorMes:data.bases.ADM_LOCAL}});
        if (data.ajustes) setAjustes(data.ajustes);
        if (data.obrasOv) setObrasOv(data.obrasOv);
        if (data.bdiComp) setBdiComp({AC:0,DF:0,R:0,CT:0,SG:0,L:0,PIS:0,COFINS:0,ISS:0,CPRB:0, ...data.bdiComp});
        if (data.bdiMeses) setBdiMeses({DF:0, SG:0, ...data.bdiMeses});
        // v28 · detectar BDI agregado do MEF e oferecer auto-decomposição
        const bdiMefDetectado = detectarBdiDoMef(data);
        if (bdiMefDetectado && !data.bdiComp){
          // Não há componentes salvos · aplicar auto-decomposição
          aplicarBdiDoMef(bdiMefDetectado);
        } else if (bdiMefDetectado){
          // Apenas mostrar a referência readonly
          setBdiMefRef(bdiMefDetectado);
        }
        if (data.abcServOv) setAbcServOv(data.abcServOv);
        if (data.abcInsLista) setAbcInsLista(data.abcInsLista);
      } catch (err) {
        if (typeof window !== 'undefined') window.alert('JSON inválido: '+(err.message||''));
      }
    };
    reader.readAsText(file); e.target.value = '';
  };
  const resetar = () => {
    if (typeof window === 'undefined') return;
    if (window.confirm('Resetar TUDO deste lote?')) {
      setIndiretos(_initIndiretos); setAjustes({}); setObrasOv({});
      // v28 · default = preset MEF ANTT (~23%) em vez de zeros
      setBdiComp({AC:3.50, DF:1.00, R:1.00, CT:0.50, SG:0.50, L:8.00, PIS:0.65, COFINS:3.00, ISS:2.00, CPRB:0.00});
      setBdiMeses({DF:0, SG:0}); setAbcServOv({}); setAbcInsLista([]);
      setBdiMefRef(null); setBdiAutoAviso(false);
    }
  };

  const addInsumo = () => setAbcInsLista(l => [...l, {id:'ins-'+Date.now(), desc:'Novo insumo', classe:'A', qtd:0, und:'un', cu:0}]);
  const removeInsumo = (id) => setAbcInsLista(l => l.filter(x=>x.id!==id));
  const updateInsumo = (id, patch) => setAbcInsLista(l => l.map(x=>x.id===id?{...x,...patch}:x));
  const importInsumosJson = () => {
    if (typeof window === 'undefined') return;
    const txt = window.prompt('Cole array JSON: [{"desc":"...","classe":"A|B|C","qtd":N,"und":"...","cu":R$}, ...]', '[]');
    if (!txt) return;
    try {
      const arr = JSON.parse(txt);
      if (!Array.isArray(arr)) throw new Error('Esperado array');
      const novos = arr.map((it, i) => ({
        id: 'ins-imp-'+Date.now()+'-'+i,
        desc: String(it.desc || it.descricao || 'Insumo '+i),
        classe: ['A','B','C'].includes(it.classe) ? it.classe : 'A',
        qtd: Number(it.qtd || it.q || 0) || 0,
        und: String(it.und || it.u || 'un'),
        cu: Number(it.cu || it.preco_unit || it.p || 0) || 0,
      }));
      setAbcInsLista(l => [...l, ...novos]);
    } catch (err) { window.alert('JSON inválido: '+(err.message||'')); }
  };

  // ──────────────────────────────────────────────────────────────
  // v28.1 · Q4 INSUMOS · 3 melhorias fail-loud (Cap 16 anti-aluci)
  // ──────────────────────────────────────────────────────────────

  // Catálogo de insumos típicos rodoviários (FONTE: SICRO 2024 + uso comum DNIT)
  // CU médios são ESTIMATIVAS de mercado · usuário deve ajustar com SICRO oficial
  const INSUMOS_TIPICOS_RODOVIA = [
    {desc:'CAP 50/70 (cimento asfáltico de petróleo)', und:'t',  cu:4200, classe:'A', categoria:'pavimentação'},
    {desc:'Aço CA-50 D≥10mm',                          und:'kg', cu:8.5,  classe:'A', categoria:'estrutural'},
    {desc:'Aço CA-50 D=12,5mm (cordoalha CP-190)',     und:'kg', cu:14.2, classe:'A', categoria:'estrutural'},
    {desc:'Concreto fck=25 MPa (usinado)',             und:'m³', cu:520,  classe:'A', categoria:'estrutural'},
    {desc:'Concreto fck=30 MPa (usinado)',             und:'m³', cu:580,  classe:'A', categoria:'estrutural'},
    {desc:'Brita comercial nº 1 (gnaisse/granito)',    und:'m³', cu:115,  classe:'A', categoria:'pavimentação'},
    {desc:'Brita graduada simples',                    und:'m³', cu:128,  classe:'A', categoria:'pavimentação'},
    {desc:'Areia comercial natural',                   und:'m³', cu:95,   classe:'A', categoria:'pavimentação'},
    {desc:'Cimento Portland CP-II-32 (saco 50kg)',     und:'sc', cu:39,   classe:'A', categoria:'estrutural'},
    {desc:'Emulsão asfáltica RR-1C',                   und:'kg', cu:5.8,  classe:'A', categoria:'pavimentação'},
    {desc:'Defensa metálica simples (perfil W)',       und:'m',  cu:158,  classe:'B', categoria:'sinalização'},
    {desc:'Tubo de concreto armado D=1,00m PA2',       und:'m',  cu:285,  classe:'B', categoria:'drenagem'},
    {desc:'Geotêxtil não-tecido 14kN/m',               und:'m²', cu:6.5,  classe:'B', categoria:'geotécnico'},
    {desc:'Tela de aço eletrossoldada Q138',           und:'m²', cu:21,   classe:'B', categoria:'estrutural'},
    {desc:'Tinta acrílica para sinalização horizontal',und:'kg', cu:14.8, classe:'C', categoria:'sinalização'},
  ];

  // Decomposições típicas de mercado (regras de bolso · NÃO oficiais)
  // Aplicadas sobre Top serviços SICRO para gerar lista preliminar de insumos
  const DECOMPOSICOES_TIPICAS = {
    // Pavimentação asfáltica (CBUQ): CAP é insumo dominante
    pavimentacao_asfaltica: [
      {desc:'CAP 50/70', und:'t',  pct:0.55, cuRef:4200, classe:'A'},
      {desc:'Brita produzida (Faixa C)', und:'m³', pct:0.20, cuRef:115, classe:'A'},
      {desc:'Areia natural', und:'m³', pct:0.10, cuRef:95, classe:'A'},
      {desc:'Mão-de-obra + equipamentos', und:'h',  pct:0.15, cuRef:0,  classe:'B'},
    ],
    // Concreto estrutural (OAE)
    concreto_estrutural: [
      {desc:'Aço CA-50',     und:'kg', pct:0.30, cuRef:8.5,  classe:'A'},
      {desc:'Cimento CP-II', und:'sc', pct:0.25, cuRef:39,   classe:'A'},
      {desc:'Brita comercial nº1', und:'m³', pct:0.15, cuRef:115, classe:'A'},
      {desc:'Areia natural', und:'m³', pct:0.10, cuRef:95,   classe:'A'},
      {desc:'Mão-de-obra + fôrmas', und:'h', pct:0.20, cuRef:0, classe:'B'},
    ],
    // Drenagem (BSTC, BSCC, sarjetas)
    drenagem_concreto: [
      {desc:'Tubo concreto armado',  und:'m', pct:0.50, cuRef:285, classe:'B'},
      {desc:'Concreto magro fck=15', und:'m³', pct:0.20, cuRef:380, classe:'A'},
      {desc:'Aço CA-50 D≥10mm',      und:'kg', pct:0.15, cuRef:8.5, classe:'A'},
      {desc:'Mão-de-obra + escavação', und:'h', pct:0.15, cuRef:0,  classe:'B'},
    ],
    // Terraplenagem (escavação/aterro)
    terraplenagem: [
      {desc:'Equipamentos pesados (escavadeira/caminhão)', und:'h', pct:0.45, cuRef:0, classe:'B'},
      {desc:'Combustível diesel', und:'l', pct:0.30, cuRef:6.2, classe:'A'},
      {desc:'Mão-de-obra operacional', und:'h', pct:0.25, cuRef:0,  classe:'B'},
    ],
    // Sinalização (tinta, defensa, placa)
    sinalizacao: [
      {desc:'Tinta acrílica',       und:'kg', pct:0.40, cuRef:14.8, classe:'C'},
      {desc:'Defensa metálica W',   und:'m',  pct:0.40, cuRef:158, classe:'B'},
      {desc:'Mão-de-obra + transporte', und:'h', pct:0.20, cuRef:0,   classe:'B'},
    ],
  };

  // Heurística: identifica categoria do serviço SICRO pela descrição
  const categoriaPorServico = (descricao) => {
    const d = (descricao||'').toLowerCase();
    if (/asfáltico|cbuq|asfalto|pintura de ligação|imprimação|fresagem/.test(d)) return 'pavimentacao_asfaltica';
    if (/concreto.*fck|estrutura|forma|fôrma|armadura|aço ca-50|protendido/.test(d)) return 'concreto_estrutural';
    if (/dren|sarjeta|bueiro|valeta|bstc|bscc|bdtc/.test(d)) return 'drenagem_concreto';
    if (/escavação|aterro|terrapl|compactação|bota-fora|escavar/.test(d)) return 'terraplenagem';
    if (/sinaliz|defensa|placa|tinta|tachão|tachinha|barreira/.test(d)) return 'sinalizacao';
    return null; // sem decomposição típica
  };

  // FUNÇÃO B · Pre-popular insumos típicos com aviso fail-loud
  const prePopularInsumosTipicos = () => {
    if (typeof window === 'undefined') return;
    if (abcInsLista.length > 0){
      if (!window.confirm('Já há '+abcInsLista.length+' insumo(s) cadastrados. Adicionar os típicos por cima?')) return;
    }
    const novos = INSUMOS_TIPICOS_RODOVIA.map((ins, i) => ({
      id: 'ins-tipico-'+Date.now()+'-'+i,
      desc: '⚡ '+ins.desc+' [pré-pop]',
      classe: ins.classe,
      qtd: 0, // qtd zerada · usuário deve preencher
      und: ins.und,
      cu: ins.cu,
      _origem: 'pre-pop-tipico',
      _aviso: 'CU estimado de mercado · ajuste com SICRO oficial',
    }));
    setAbcInsLista(l => [...l, ...novos]);
    window.alert(novos.length+' insumos típicos adicionados.\n\nATENÇÃO (Cap 16):\n· Quantidades ZERADAS · você deve preencher\n· CUs são estimados de mercado · valide com SICRO oficial\n· Os itens estão marcados [pré-pop] para você identificar');
  };

  // FUNÇÃO C · Decompor insumos a partir do TOP serviços SICRO
  const decomporInsumosDoSicro = () => {
    if (typeof window === 'undefined') return;
    if (!top10ABCServ || top10ABCServ.length === 0){
      window.alert('Q3 · TOP-10 Serviços SICRO está vazio · não há base para decomposição');
      return;
    }
    if (abcInsLista.length > 0){
      if (!window.confirm('Já há '+abcInsLista.length+' insumo(s) cadastrados. Adicionar decomposição por cima?')) return;
    }
    const novos = [];
    const decomposicoesPorInsumo = {}; // agrupar por descrição

    for (const serv of top10ABCServ){
      const cat = categoriaPorServico(serv.desc);
      if (!cat) continue; // serviço sem decomposição típica
      const tabela = DECOMPOSICOES_TIPICAS[cat];
      if (!tabela) continue;
      const valorServico = serv.totalRev || serv.total || 0;
      for (const insumo of tabela){
        const valorAlocado = valorServico * insumo.pct;
        const qtdEstim = insumo.cuRef > 0 ? valorAlocado / insumo.cuRef : 0;
        const key = insumo.desc;
        if (!decomposicoesPorInsumo[key]){
          decomposicoesPorInsumo[key] = {desc:'⚡ '+insumo.desc+' [decomp-Q3]', und:insumo.und, cu:insumo.cuRef, classe:insumo.classe, qtd:0, _origem:[]};
        }
        decomposicoesPorInsumo[key].qtd += qtdEstim;
        decomposicoesPorInsumo[key]._origem.push(serv.c+'·'+(insumo.pct*100).toFixed(0)+'%');
      }
    }
    let i = 0;
    for (const [_, ins] of Object.entries(decomposicoesPorInsumo)){
      novos.push({
        id: 'ins-decomp-'+Date.now()+'-'+(i++),
        desc: ins.desc,
        classe: ins.classe,
        qtd: Math.round(ins.qtd*100)/100,
        und: ins.und,
        cu: ins.cu,
        _origem: 'decomp-Q3',
        _origemDetalhe: ins._origem.join(','),
      });
    }
    if (novos.length === 0){
      window.alert('Nenhum serviço do TOP-10 tem decomposição típica mapeada (apenas pavimentação, concreto, drenagem, terraplenagem e sinalização têm regras).');
      return;
    }
    setAbcInsLista(l => [...l, ...novos]);
    window.alert(novos.length+' insumos decompostos do TOP-10 SICRO.\n\n⚠ ATENÇÃO (Cap 16 fail-loud):\nDecomposição usa REGRAS DE BOLSO do mercado, NÃO o MEF do lote:\n· Pavimentação: 55% CAP + 20% brita + 10% areia + 15% MO\n· Concreto: 30% aço + 25% cimento + 15% brita + 10% areia + 20% MO\n· Drenagem: 50% tubo + 20% concreto + 15% aço + 15% MO\nValide caso a caso. Itens marcados [decomp-Q3].');
  };

  // ── ESTILOS ──────────────────────────────────────────────────
  const COL_BASE = '#eff6ff';
  const COL_AJ   = '#FDF0E8';
  const COL_REV  = '#f0fdf4';
  const COL_QTD  = '#fffbeb';
  const COL_ACUM = '#f5f3ff';
  const COL_WARN = '#fef3c7';
  // ── v28.3 · Auditoria de Preenchimento (auto-disparo + reverificação manual) ──
  const [auditoria, setAuditoria] = useState(null);
  const [auditoriaExpandida, setAuditoriaExpandida] = useState(false);
  const [auditoriaRodando, setAuditoriaRodando] = useState(false);

  const rodarAuditoria = useCallback(async () => {
    setAuditoriaRodando(true);
    const ctx = {
      sig, lote, cf:CONSOL_FIN[sig]||{}, od, ext,
      indiretos, ajustes, obrasOv,
      bdiComp, bdiMeses, bdiCalculado,
      abcServOv, abcInsLista,
      custoDiretoEdital, custoDiretoManta, totalGeralEdital, totalGeralManta,
      linhasObras, linhasIndiretos, top10ABCServ,
      // KPIs do Novo Estudo (se houver na sessão · localStorage)
      qual: (()=>{ try { return JSON.parse(window.localStorage.getItem('manta-novo-estudo-qual')||'{}'); } catch { return {}; } })(),
      kpis: (()=>{ try { return JSON.parse(window.localStorage.getItem('manta-novo-estudo-kpis')||'{}'); } catch { return {}; } })(),
    };
    try {
      const resultados = await executarPipeline('auditoriaPreenchimento', ctx, ()=>{});
      setAuditoria(resultados.validador);
    } catch(e) {
      setAuditoria({status:'erro', msg:'Falha ao auditar: '+e.message});
    }
    setAuditoriaRodando(false);
  }, [sig, lote, ext, indiretos, ajustes, obrasOv, bdiComp, bdiMeses, bdiCalculado, abcServOv, abcInsLista, custoDiretoEdital, custoDiretoManta, totalGeralEdital, totalGeralManta]);

  // Auto-disparo: 1 vez ao montar / sempre que sig (lote) mudar
  useEffect(() => {
    rodarAuditoria();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const corDelta = (rev, base) => rev>base?V.or:(rev<base?V.gn:V.br);
  const corClasse = (cls) => cls==='A'?V.or : (cls==='B'?(V.am||'#d97706') : V.gn);

  // ── v28.2 · Helpers para coluna Δ (Manta vs Edital) ──────────
  const deltaColor = (manta, edital) => {
    if (edital == null || manta == null) return V.gr;
    if (manta < edital) return V.gn;
    if (manta > edital) return V.or;
    return V.gr;
  };
  const fmtDeltaRS = (manta, edital) => {
    if (edital == null || manta == null) return '—';
    const d = manta - edital;
    if (Math.abs(d) < 0.01) return 'R$ 0';
    return (d>0?'+':'') + fmtBRL(d, 0);
  };
  const fmtDeltaPct = (manta, edital) => {
    if (!edital || edital === 0) return '—';
    if (manta == null) return '—';
    const p = (manta - edital) / edital * 100;
    if (Math.abs(p) < 0.01) return '0,0%';
    return (p>0?'+':'') + p.toFixed(1).replace('.',',') + '%';
  };
  const deltaCellBg = (manta, edital) => {
    if (edital == null || manta == null) return 'transparent';
    const diff = manta - edital;
    if (Math.abs(diff) < 0.01) return 'transparent';
    return diff < 0 ? '#F0FDF4' : '#FEF2F2';
  };
  const COL_DELTA = '#fef9c3';

  const inputN = {fontSize:10.5,padding:'3px 6px',border:'1px solid '+V.b1,borderRadius:3,textAlign:'right',fontFamily:'ui-monospace,monospace',outline:'none'};
  const HQ = (n,t,sub,warn)=> (<div style={{padding:'10px 14px',background: warn ? 'linear-gradient(90deg,#7c2d12,#a3531e)' : 'linear-gradient(90deg,#1E2A4A,#2D3F6B)',color:'#F4A76A',fontSize:11,fontWeight:800,letterSpacing:'.05em',textTransform:'uppercase',display:'flex',alignItems:'center',gap:8}}>
    <span style={{width:24,height:24,background:V.or,color:'#fff',borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800}}>{n}</span>
    <span>Quadro {n} · {t}</span>
    {sub && <span style={{fontSize:9.5,color:'rgba(255,255,255,.55)',fontWeight:500,textTransform:'none',letterSpacing:'.02em'}}>{sub}</span>}
    {warn && <span style={{marginLeft:'auto',fontSize:10,color:'#FCD34D',background:'rgba(0,0,0,.25)',padding:'2px 8px',borderRadius:3,fontWeight:700}}>⚠ DUPLA CONTAGEM</span>}
  </div>);

  return (<div style={{padding:'14px 17px'}}>
    <div style={{...S.h1,marginBottom:4}}>A · Resumo do Orçamento Paramétrico Manta · {lote.nome}</div>
    <div style={{...S.sub,marginBottom:12}}>
      Q1 Custo Direto detalhado · Q2 BDI + Total Geral · Q3/Q4 TOP-10 Curva A com edição de Q e CU. Ajustes em Q3/Q4 propagam o Δ ao Custo Direto.
    </div>

    {/* v28.1 · Banner de integridade dos dados (Cap 16 fail-loud) */}
    {(() => {
      const cf = CONSOL_FIN[sig] || {};
      const totalSicro = Object.values(cf).reduce((s, arr) => s + (arr?.length || 0), 0);
      if (totalSicro === 0){
        return (
          <div style={{padding:'10px 14px',marginBottom:12,background:'#FEF2F2',border:'2px solid #EF4444',borderRadius:6,fontSize:11,color:'#991B1B',display:'flex',alignItems:'center',gap:10}}>
            <span style={{fontSize:18}}>⚠</span>
            <div style={{flex:1}}>
              <b>CONSOL_FIN[{sig}] vazio · Q3 e Q4 não vão funcionar.</b><br/>
              Este lote não tem dados SICRO embarcados no portal. Se você esperava ver TOP-10 Serviços, contate o admin do portal.
            </div>
          </div>
        );
      }
      // Quando há dados, mostrar status discreto verde
      return (
        <div style={{padding:'6px 12px',marginBottom:10,background:'#F0FDF4',border:'1px solid '+V.gn,borderRadius:4,fontSize:10,color:V.br,display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:11,color:V.gn}}>●</span>
          <span><b>Integridade OK</b> · {totalSicro} itens SICRO em CONSOL_FIN[{sig}] · Q3 com dados · Q4 vazio por design (use ⚡ Pré-pop ou 🔬 Decompor)</span>
        </div>
      );
    })()}

    {/* ═══ v28.3 · PAINEL AUDITORIA DE PREENCHIMENTO (auto-disparo) ═══ */}
    {(() => {
      const a = auditoria;
      const corStatus = !a ? V.gr : ({
        ok:V.gn, atencao:'#f59e0b', incompleto:'#3b82f6', critico:V.or, erro:V.or
      })[a.status] || V.gr;
      const bgStatus = !a ? '#f9fafb' : ({
        ok:'#F0FDF4', atencao:'#FEF3C7', incompleto:'#EFF6FF', critico:'#FEF2F2', erro:'#FEF2F2'
      })[a.status] || '#f9fafb';
      const iconStatus = !a ? '⏳' : ({
        ok:'✓', atencao:'⚠', incompleto:'◐', critico:'✗', erro:'⚠'
      })[a.status] || '?';

      return (
        <div style={{marginBottom:14, border:'2px solid '+corStatus, borderRadius:8, background:bgStatus, overflow:'hidden'}}>
          {/* Header sempre visível */}
          <div onClick={()=>setAuditoriaExpandida(!auditoriaExpandida)} style={{padding:'10px 14px',display:'flex',alignItems:'center',gap:12,cursor:'pointer',userSelect:'none'}}>
            <span style={{fontSize:18,color:corStatus,fontWeight:800}}>{auditoriaRodando?'⏳':iconStatus}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:12,fontWeight:800,color:V.br,letterSpacing:.3}}>
                🧪 Agente Validador de Preenchimento
                {a && <span style={{marginLeft:10,fontSize:11,fontWeight:700,color:corStatus}}>· score {a.score}%</span>}
                {auditoriaRodando && <span style={{marginLeft:8,fontSize:10,fontStyle:'italic',color:V.gr}}>auditando...</span>}
              </div>
              <div style={{fontSize:10.5,color:V.gr,marginTop:2}}>
                {auditoriaRodando ? 'Validando todos os 4 quadros + KPIs do Novo Estudo via pipeline auditoriaPreenchimento...' :
                  (a?.msg || 'Aguardando primeira auditoria...')}
              </div>
            </div>
            <button
              onClick={(e)=>{e.stopPropagation(); rodarAuditoria();}}
              disabled={auditoriaRodando}
              style={{padding:'5px 12px',fontSize:10.5,fontWeight:700,border:'1px solid '+corStatus,background:'#fff',color:corStatus,borderRadius:4,cursor:auditoriaRodando?'not-allowed':'pointer',opacity:auditoriaRodando?0.5:1}}
              title="Reverificar com os dados atuais"
            >↻ Reverificar</button>
            <span style={{fontSize:14,color:V.gr,transform:auditoriaExpandida?'rotate(180deg)':'none',transition:'transform .15s'}}>▼</span>
          </div>

          {/* Conteúdo expandido */}
          {auditoriaExpandida && a && a.secoes && (
            <div style={{padding:'12px 16px',borderTop:'1px solid '+V.b1,background:'#fff'}}>

              {/* Tabela de seções */}
              <div style={{fontSize:10,fontWeight:800,color:V.br,letterSpacing:.4,textTransform:'uppercase',marginBottom:6}}>📊 Score por seção (peso ponderado)</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5,marginBottom:14}}>
                <thead><tr style={{background:V.g0,borderBottom:'1px solid '+V.b1}}>
                  <th style={{padding:'5px 8px',textAlign:'left',fontSize:9.5,fontWeight:700,color:V.gr,letterSpacing:.3}}>Seção</th>
                  <th style={{padding:'5px 8px',textAlign:'right',fontSize:9.5,fontWeight:700,color:V.gr}}>Peso</th>
                  <th style={{padding:'5px 8px',textAlign:'right',fontSize:9.5,fontWeight:700,color:V.gr}}>Preench.</th>
                  <th style={{padding:'5px 8px',textAlign:'right',fontSize:9.5,fontWeight:700,color:V.gr}}>%</th>
                  <th style={{padding:'5px 8px',textAlign:'left',fontSize:9.5,fontWeight:700,color:V.gr}}>Faltas</th>
                </tr></thead>
                <tbody>
                  {a.secoes.map((s,i) => {
                    const corPct = s.pct >= 90 ? V.gn : (s.pct >= 60 ? '#f59e0b' : V.or);
                    return (<tr key={i} style={{borderBottom:'1px solid '+V.g1}}>
                      <td style={{padding:'4px 8px',fontWeight:600,color:V.br}}>{s.label}</td>
                      <td style={{padding:'4px 8px',textAlign:'right',color:V.gr,fontFamily:'monospace',fontSize:10}}>{s.peso}</td>
                      <td style={{padding:'4px 8px',textAlign:'right',color:V.gr,fontFamily:'monospace',fontSize:10}}>{s.completo}/{s.total}</td>
                      <td style={{padding:'4px 8px',textAlign:'right',fontWeight:700,color:corPct,fontFamily:'monospace'}}>{s.pct}%</td>
                      <td style={{padding:'4px 8px',fontSize:9.5,color:V.gr,fontStyle:'italic'}}>
                        {s.faltas && s.faltas.length > 0
                          ? (s.faltas.length > 3 ? s.faltas.slice(0,3).join(' · ')+' · +'+(s.faltas.length-3)+' itens' : s.faltas.join(' · '))
                          : '—'}
                      </td>
                    </tr>);
                  })}
                </tbody>
              </table>

              {/* Lista acionável */}
              {a.lista_acionavel && a.lista_acionavel.length > 0 && (<>
                <div style={{fontSize:10,fontWeight:800,color:V.br,letterSpacing:.4,textTransform:'uppercase',marginBottom:6}}>🎯 Ações sugeridas</div>
                <div style={{marginBottom:14}}>
                  {a.lista_acionavel.map((acao,i) => {
                    const corSev = ({critica:V.or, alta:'#dc2626', media:'#f59e0b', baixa:V.bl})[acao.severidade] || V.gr;
                    const bgSev = ({critica:'#FEF2F2', alta:'#FEF2F2', media:'#FEF3C7', baixa:'#EFF6FF'})[acao.severidade] || '#f9fafb';
                    return (<div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 10px',marginBottom:4,background:bgSev,borderLeft:'3px solid '+corSev,borderRadius:'0 4px 4px 0',fontSize:11}}>
                      <span style={{fontSize:10,fontWeight:800,color:corSev,letterSpacing:.3,textTransform:'uppercase',minWidth:60}}>{acao.severidade}</span>
                      <span style={{fontWeight:700,color:V.br,minWidth:120}}>{acao.secao}</span>
                      <span style={{flex:1,color:V.br}}>{acao.msg}</span>
                    </div>);
                  })}
                </div>
              </>)}

              {/* Benchmarks */}
              {a.benchmarks && a.benchmarks.length > 0 && (<>
                <div style={{fontSize:10,fontWeight:800,color:V.br,letterSpacing:.4,textTransform:'uppercase',marginBottom:6}}>📈 Benchmarks</div>
                <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:8}}>
                  {a.benchmarks.map((b,i) => (
                    <div key={i} style={{padding:'8px 12px',background:V.g0,border:'1px solid '+V.b1,borderRadius:4,fontSize:10.5,minWidth:160}}>
                      <div style={{fontSize:9.5,color:V.gr,letterSpacing:.3,textTransform:'uppercase',fontWeight:700}}>{b.metrica}</div>
                      <div style={{fontSize:13,fontWeight:800,color:V.br,fontFamily:'monospace',marginTop:2}}>{b.fmt}</div>
                    </div>
                  ))}
                </div>
              </>)}

              <div style={{fontSize:9,color:V.gr,fontStyle:'italic',marginTop:8,paddingTop:6,borderTop:'1px dashed '+V.b1}}>
                ⏱ Auditoria executada em {new Date(a.timestamp).toLocaleString('pt-BR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit'})} via pipeline <code style={{background:V.g0,padding:'1px 4px',borderRadius:2}}>auditoriaPreenchimento → validador</code> · auto-disparo no carregamento + reverificação manual.
              </div>
            </div>
          )}

          {/* Hint quando colapsado */}
          {!auditoriaExpandida && a && a.lista_acionavel && a.lista_acionavel.length > 0 && (
            <div style={{padding:'4px 14px 8px',fontSize:10,color:V.gr}}>
              👆 Clique para ver as <b style={{color:corStatus}}>{a.lista_acionavel.length} ação(ões)</b> sugeridas e o detalhamento por seção
            </div>
          )}
        </div>
      );
    })()}

    {/* TOOLBAR STICKY */}
    <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center',flexWrap:'wrap',position:'sticky',top:0,zIndex:5,padding:'8px 0',background:V.g0,borderBottom:'1px solid '+V.b1}}>
      <button onClick={exportar} style={{padding:'6px 14px',fontSize:11,fontWeight:700,border:'1.5px solid '+V.bl,background:'#fff',color:V.bl,borderRadius:5,cursor:'pointer'}}>⬇ Exportar JSON</button>
      <label style={{padding:'6px 14px',fontSize:11,fontWeight:700,border:'1.5px solid '+V.bl,background:'#fff',color:V.bl,borderRadius:5,cursor:'pointer',userSelect:'none'}}>
        ⬆ Importar JSON
        <input type="file" accept="application/json,.json" onChange={importar} style={{display:'none'}}/>
      </label>
      <button onClick={resetar} style={{padding:'6px 14px',fontSize:11,fontWeight:700,border:'1.5px solid '+V.gr,background:'#fff',color:V.gr,borderRadius:5,cursor:'pointer'}}>↺ Resetar tudo</button>
      <span style={{marginLeft:'auto',fontSize:10,color:V.gr,fontStyle:'italic'}}>Salvo em <code style={{background:'#fff',padding:'1px 5px',borderRadius:3,fontSize:9.5,border:'1px solid '+V.b1}}>{SK}</code></span>
    </div>

    {/* ═══ QUADRO 1 · CUSTO DIRETO DETALHADO ═══ */}
    <div style={{...S.card,overflow:'auto',marginBottom:18,border:'2px solid '+V.or}}>
      {HQ(1, 'Custo Direto Detalhado', 'Obras 1.2.x (Q×CU) + Indiretos (meses×R$/mês) + Δ Q3 + Δ Q4', duplaContagem)}

      {/* 1.A · Obras 1.2.x */}
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
        <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
          <th style={{...THl,width:70}}>Cód</th>
          <th style={THl}>Descrição</th>
          <th style={{...TH,width:90,background:COL_QTD}}>Qtde</th>
          <th style={{...TH,width:55,background:COL_QTD}}>Und</th>
          <th style={{...TH,width:110,background:COL_BASE}}>CU (R$)</th>
          <th style={{...TH,width:120,background:COL_BASE,color:V.bl}}>Edital (Total)</th>
          <th style={{...TH,width:140,background:COL_AJ,color:V.or}}>Ajuste %/R$</th>
          <th style={{...TH,width:120,background:COL_REV,color:V.gn}}>Manta Revisado</th>
          <th style={{...TH,width:110,background:COL_DELTA,color:'#78350F'}}>Δ R$</th>
          <th style={{...TH,width:75,background:COL_DELTA,color:'#78350F'}}>Δ %</th>
        </tr></thead>
        <tbody>
          <tr style={{background:V.g1}}><td colSpan={10} style={{padding:'7px 12px',fontSize:10,fontWeight:800,color:V.br,textTransform:'uppercase',letterSpacing:'.05em'}}>1.A · Obras de Ampliação · 1.2.x · {linhasObras.length} itens · Q×CU sobrescreve o Edital quando ambos preenchidos</td></tr>
          {linhasObras.map(l => {
            const ov = obrasOv[l.cod] || {};
            return (<tr key={l.key} style={{...S.trLine, background: l.touched ? '#fffaf0' : undefined}}>
              <td style={{...TDl,fontFamily:'ui-monospace,monospace',fontWeight:700}}>{l.cod}</td>
              <td style={TDl}>{l.desc}</td>
              <td style={{...TD,padding:'3px 6px',background:COL_QTD}}>
                <input type="number" step="0.01" value={ov.qtd ?? ''} onChange={e=>{const v=e.target.value===''?null:(parseFloat(e.target.value)||0); setObrasOv(o=>({...o,[l.cod]:{...o[l.cod],qtd:v}}));}} placeholder={l.qOrig != null ? fmtNum(l.qOrig, l.undOrig==='km'?1:0) : '—'} style={{...inputN,width:'100%',fontSize:10}}/>
              </td>
              <td style={{...TD,padding:'3px 4px',background:COL_QTD}}>
                <input type="text" value={ov.und ?? ''} onChange={e=>setObrasOv(o=>({...o,[l.cod]:{...o[l.cod],und:e.target.value||null}}))} placeholder={l.undOrig} style={{width:'100%',fontSize:10,padding:'3px 4px',border:'1px solid '+V.b1,borderRadius:3,textAlign:'center',outline:'none'}}/>
              </td>
              <td style={{...TD,padding:'3px 6px',background:COL_BASE}}>
                <input type="number" step="0.01" value={ov.cu ?? ''} onChange={e=>{const v=e.target.value===''?null:(parseFloat(e.target.value)||0); setObrasOv(o=>({...o,[l.cod]:{...o[l.cod],cu:v}}));}} placeholder={l.cuOrig != null ? fmtNum(l.cuOrig, 2) : '—'} style={{...inputN,width:'100%',fontSize:10}}/>
              </td>
              <td style={{...TD,background:COL_BASE,fontWeight:600,color:V.gr,fontSize:10}}>{l.editalTotal?fmtBRL(l.editalTotal,0):'—'}</td>
              <td style={{...TD,padding:'3px 8px',background:COL_AJ}}>
                <AjusteInput aj={l.aj} onChange={p=>setAjustes(a=>({...a, [l.key]:{...(a[l.key]||{tipo:'%',valor:0}), ...p}}))}/>
              </td>
              <td style={{...TD,fontWeight:700,color:corDelta(l.totalRev,l.editalTotal),background:COL_REV}}>{l.totalRev?fmtBRL(l.totalRev,0):'—'}</td>
              <td style={{...TD,fontWeight:700,fontSize:10,fontFamily:'monospace',color:deltaColor(l.totalRev,l.editalTotal),background:deltaCellBg(l.totalRev,l.editalTotal)}}>{fmtDeltaRS(l.totalRev,l.editalTotal)}</td>
              <td style={{...TD,fontWeight:700,fontSize:10,fontFamily:'monospace',color:deltaColor(l.totalRev,l.editalTotal),background:deltaCellBg(l.totalRev,l.editalTotal)}}>{fmtDeltaPct(l.totalRev,l.editalTotal)}</td>
            </tr>);
          })}
          <tr style={{background:V.g0,borderTop:'1px solid '+V.b1}}>
            <td colSpan={5} style={{...TDl,fontWeight:800,color:V.br}}>Subtotal · Obras de Ampliação</td>
            <td style={{...TD,fontWeight:800,color:V.br,background:COL_BASE}}>{fmtBRL(subtotalObrasEdital,0)}</td>
            <td style={{...TD,background:COL_AJ,color:V.gr,fontStyle:'italic'}}>—</td>
            <td style={{...TD,fontWeight:800,color:corDelta(subtotalObrasRev,subtotalObrasEdital),background:COL_REV}}>{fmtBRL(subtotalObrasRev,0)}</td>
            <td style={{...TD,fontWeight:800,fontSize:10.5,fontFamily:'monospace',color:deltaColor(subtotalObrasRev,subtotalObrasEdital),background:deltaCellBg(subtotalObrasRev,subtotalObrasEdital)}}>{fmtDeltaRS(subtotalObrasRev,subtotalObrasEdital)}</td>
            <td style={{...TD,fontWeight:800,fontSize:10.5,fontFamily:'monospace',color:deltaColor(subtotalObrasRev,subtotalObrasEdital),background:deltaCellBg(subtotalObrasRev,subtotalObrasEdital)}}>{fmtDeltaPct(subtotalObrasRev,subtotalObrasEdital)}</td>
          </tr>
        </tbody>
      </table>

      {/* 1.B · Indiretos */}
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5,borderTop:'2px solid '+V.b1}}>
        <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
          <th style={{...THl,width:70}}>Sigla</th>
          <th style={THl}>Indireto</th>
          <th style={{...TH,width:90,background:COL_QTD}}>Meses</th>
          <th style={{...TH,width:120,background:COL_QTD}}>R$/mês</th>
          <th style={{...TH,width:55}}></th>
          <th style={{...TH,width:120,background:COL_BASE,color:V.bl}}>Edital (M×R$/m)</th>
          <th style={{...TH,width:140,background:COL_AJ,color:V.or}}>Ajuste %/R$</th>
          <th style={{...TH,width:120,background:COL_REV,color:V.gn}}>Manta Revisado</th>
          <th style={{...TH,width:110,background:COL_DELTA,color:'#78350F'}}>Δ R$</th>
          <th style={{...TH,width:75,background:COL_DELTA,color:'#78350F'}}>Δ %</th>
        </tr></thead>
        <tbody>
          <tr style={{background:V.g1}}><td colSpan={10} style={{padding:'7px 12px',fontSize:10,fontWeight:800,color:V.br,textTransform:'uppercase',letterSpacing:'.05em'}}>1.B · Indiretos · meses × valor unitário mensal</td></tr>
          {linhasIndiretos.map(l => (
            <tr key={l.key} style={S.trLine}>
              <td style={{...TDl,fontFamily:'ui-monospace,monospace',fontWeight:700,color:V.gr,fontSize:9.5}}>{l.key}</td>
              <td style={TDl}>{l.desc}</td>
              <td style={{...TD,padding:'3px 8px',background:COL_QTD}}>
                <input type="number" value={l.meses||''} onChange={e=>setIndiretos(o=>({...o,[l.key]:{...(o[l.key]||{}),meses:parseFloat(e.target.value)||0}}))} placeholder="meses" style={{...inputN,width:'100%'}}/>
              </td>
              <td style={{...TD,padding:'3px 8px',background:COL_QTD}}>
                <input type="number" value={l.valorMes||''} onChange={e=>setIndiretos(o=>({...o,[l.key]:{...(o[l.key]||{}),valorMes:parseFloat(e.target.value)||0}}))} placeholder="R$/mês" style={{...inputN,width:'100%'}}/>
              </td>
              <td style={TD}></td>
              <td style={{...TD,background:COL_BASE,fontWeight:700,color:V.br,fontFamily:'monospace'}}>{l.base?fmtBRL(l.base,0):'—'}</td>
              <td style={{...TD,padding:'3px 8px',background:COL_AJ}}>
                <AjusteInput aj={l.aj} onChange={p=>setAjustes(a=>({...a, [l.key]:{...(a[l.key]||{tipo:'%',valor:0}), ...p}}))}/>
              </td>
              <td style={{...TD,fontWeight:700,color:corDelta(l.revisado,l.base),background:COL_REV}}>{l.revisado?fmtBRL(l.revisado,0):'—'}</td>
              <td style={{...TD,fontWeight:700,fontSize:10,fontFamily:'monospace',color:deltaColor(l.revisado,l.base),background:deltaCellBg(l.revisado,l.base)}}>{fmtDeltaRS(l.revisado,l.base)}</td>
              <td style={{...TD,fontWeight:700,fontSize:10,fontFamily:'monospace',color:deltaColor(l.revisado,l.base),background:deltaCellBg(l.revisado,l.base)}}>{fmtDeltaPct(l.revisado,l.base)}</td>
            </tr>
          ))}
          <tr style={{background:V.g0,borderTop:'1px solid '+V.b1}}>
            <td colSpan={5} style={{...TDl,fontWeight:800,color:V.br}}>Subtotal · Indiretos</td>
            <td style={{...TD,fontWeight:800,color:V.br,background:COL_BASE}}>{subtotalIndBase?fmtBRL(subtotalIndBase,0):'—'}</td>
            <td style={{...TD,background:COL_AJ,color:V.gr,fontStyle:'italic'}}>—</td>
            <td style={{...TD,fontWeight:800,color:corDelta(subtotalIndRev,subtotalIndBase),background:COL_REV}}>{subtotalIndRev?fmtBRL(subtotalIndRev,0):'—'}</td>
            <td style={{...TD,fontWeight:800,fontSize:10.5,fontFamily:'monospace',color:deltaColor(subtotalIndRev,subtotalIndBase),background:deltaCellBg(subtotalIndRev,subtotalIndBase)}}>{fmtDeltaRS(subtotalIndRev,subtotalIndBase)}</td>
            <td style={{...TD,fontWeight:800,fontSize:10.5,fontFamily:'monospace',color:deltaColor(subtotalIndRev,subtotalIndBase),background:deltaCellBg(subtotalIndRev,subtotalIndBase)}}>{fmtDeltaPct(subtotalIndRev,subtotalIndBase)}</td>
          </tr>

          {/* Δ Q3 · Δ Q4 */}
          <tr style={{borderTop:'2px solid '+V.b1, background: q3Touched && duplaContagem ? COL_WARN : V.g0}}>
            <td colSpan={5} style={{...TDl,fontWeight:700,color:V.br,fontSize:10}}>
              Δ TOP-10 Serviços SICRO (Q3) {q3Touched && duplaContagem && <span style={{fontSize:9,color:'#92400e',fontWeight:700,marginLeft:6}}>⚠ pode duplicar</span>}
            </td>
            <td style={{...TD,fontWeight:600,color:V.gr,fontSize:10,background:COL_BASE,fontStyle:'italic'}}>{fmtBRL(top10ServBase,0)}</td>
            <td style={{...TD,fontWeight:700,color:deltaQ3>0?V.or:(deltaQ3<0?V.gn:V.gr),background:COL_AJ,fontFamily:'monospace'}}>{deltaQ3>=0?'+':''}{fmtBRL(deltaQ3,0).replace('R$','').trim()}</td>
            <td style={{...TD,fontWeight:700,color:deltaQ3>0?V.or:(deltaQ3<0?V.gn:V.br),background:COL_REV,fontFamily:'monospace'}}>{fmtBRL(top10ServRev,0)}</td>
            <td style={{...TD,fontWeight:700,fontSize:10,fontFamily:'monospace',color:deltaColor(top10ServRev,top10ServBase),background:deltaCellBg(top10ServRev,top10ServBase)}}>{fmtDeltaRS(top10ServRev,top10ServBase)}</td>
            <td style={{...TD,fontWeight:700,fontSize:10,fontFamily:'monospace',color:deltaColor(top10ServRev,top10ServBase),background:deltaCellBg(top10ServRev,top10ServBase)}}>{fmtDeltaPct(top10ServRev,top10ServBase)}</td>
          </tr>
          <tr style={{background: q4Touched && duplaContagem ? COL_WARN : V.g0}}>
            <td colSpan={5} style={{...TDl,fontWeight:700,color:V.br,fontSize:10}}>
              Δ TOP-10 Insumos (Q4) {q4Touched && duplaContagem && <span style={{fontSize:9,color:'#92400e',fontWeight:700,marginLeft:6}}>⚠ pode duplicar</span>}
            </td>
            <td style={{...TD,fontWeight:600,color:V.gr,fontSize:10,background:COL_BASE,fontStyle:'italic'}}>R$ 0</td>
            <td style={{...TD,fontWeight:700,color:deltaQ4>0?V.or:V.gr,background:COL_AJ,fontFamily:'monospace'}}>{deltaQ4>0?'+':''}{deltaQ4?fmtBRL(deltaQ4,0).replace('R$','').trim():'0'}</td>
            <td style={{...TD,fontWeight:700,color:deltaQ4>0?V.or:V.br,background:COL_REV,fontFamily:'monospace'}}>{deltaQ4?fmtBRL(deltaQ4,0):'—'}</td>
            <td style={{...TD,fontWeight:700,fontSize:10,fontFamily:'monospace',color:deltaColor(deltaQ4,0),background:deltaCellBg(deltaQ4,0)}}>{fmtDeltaRS(deltaQ4,0)}</td>
            <td style={{...TD,fontSize:9.5,color:V.gr,fontStyle:'italic',background:'transparent'}}>n/a</td>
          </tr>

          {/* CUSTO DIRETO total */}
          <tr style={{background:'#FDF0E8',borderTop:'3px solid '+V.or}}>
            <td colSpan={5} style={{...TDl,fontWeight:800,color:V.or,fontSize:12,padding:'10px 12px'}}>CUSTO DIRETO · Edital</td>
            <td style={{...TD,fontWeight:800,color:V.or,fontSize:12,fontFamily:'monospace',padding:'10px 12px'}}>{fmtBRL(custoDiretoEdital,0)}</td>
            <td style={{...TD,padding:'10px 12px'}}></td>
            <td style={{...TD,fontWeight:800,color:corDelta(custoDiretoManta,custoDiretoEdital),fontSize:12,fontFamily:'monospace',padding:'10px 12px'}}>{fmtBRL(custoDiretoManta,0)}</td>
            <td style={{...TD,fontWeight:800,fontSize:12,fontFamily:'monospace',color:deltaColor(custoDiretoManta,custoDiretoEdital),background:deltaCellBg(custoDiretoManta,custoDiretoEdital),padding:'10px 12px'}}>{fmtDeltaRS(custoDiretoManta,custoDiretoEdital)}</td>
            <td style={{...TD,fontWeight:800,fontSize:12,fontFamily:'monospace',color:deltaColor(custoDiretoManta,custoDiretoEdital),background:deltaCellBg(custoDiretoManta,custoDiretoEdital),padding:'10px 12px'}}>{fmtDeltaPct(custoDiretoManta,custoDiretoEdital)}</td>
          </tr>
        </tbody>
      </table>

      {duplaContagem && (
        <div style={{padding:'8px 14px',background:COL_WARN,borderTop:'1px solid #f59e0b',fontSize:10,color:'#78350f',display:'flex',gap:8,alignItems:'flex-start'}}>
          <span style={{fontSize:14,lineHeight:1}}>⚠</span>
          <span><b>Aviso de dupla contagem:</b> as obras 1.2.x do Q1 já contêm os serviços/insumos detalhados em Q3 e Q4. Você editou ambos. O Δ_Q3/Δ_Q4 está sendo somado ao Custo Direto Manta — verifique se essa é a sua intenção. Para evitar dupla contagem, ajuste em UMA das duas vias (alto nível Q1 OU granular Q3/Q4).</span>
        </div>
      )}
    </div>

    {/* ═══ QUADRO 2 · BDI DETALHADO + TOTAL GERAL ═══ */}
    <div style={{...S.card,overflow:'auto',marginBottom:18,border:'2px solid '+V.bl}}>
      {HQ(2, 'BDI Detalhado + Total Geral', 'Acórdão TCU 2622/2013 · Total = Custo Direto × (1 + BDI)')}

      <div style={{padding:'10px 14px',background:V.g0,borderBottom:'1px solid '+V.b1,fontSize:10,color:V.gr,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <span style={{fontWeight:700,color:V.br,fontSize:11}}>Componentes editáveis</span>
        <span style={{fontStyle:'italic'}}>BDI = ((1+AC+CT+SG+R)(1+DF)(1+L)/(1−T)) − 1 · T = PIS+COFINS+ISS+CPRB</span>
        <span style={{marginLeft:'auto',display:'flex',gap:4,alignItems:'center',flexWrap:'wrap'}}>
          {/* v28 · BDI agregado oficial MEF (referência readonly) */}
          <span style={{fontSize:9,color:V.gr,marginRight:4}}>BDI MEF oficial:</span>
          <input
            type="number"
            step="0.01"
            placeholder="ex: 22.69"
            value={bdiMefRef||''}
            onChange={e=>setBdiMefRef(parseFloat(e.target.value)||null)}
            style={{padding:'2px 6px',fontSize:9.5,width:65,border:'1px solid '+V.b1,borderRadius:3,fontFamily:'ui-monospace,monospace',color:V.gn}}
          />
          <span style={{fontSize:9,color:V.gr}}>%</span>
          <button onClick={()=>{ if(bdiMefRef) aplicarBdiDoMef(bdiMefRef); else window.alert('Preencha o BDI MEF oficial primeiro (ex: 22.69 do mef_combinado.bdi.construcao_rodoviaria)'); }} style={{padding:'2px 8px',fontSize:9.5,fontWeight:700,border:'1px solid '+V.gn,background:V.gn,color:'#fff',borderRadius:3,cursor:'pointer'}} title="Decompõe o BDI agregado proporcionalmente sobre os 6 componentes diretos do preset MEF ANTT">⚛ Auto-decompor</button>
          <span style={{marginLeft:8,fontSize:9}}>presets:</span>
          {Object.entries(presetsBDI).map(([nome, vals]) => (
            <button key={nome} onClick={()=>{ setBdiComp(vals); setBdiAutoAviso(false); }} style={{padding:'2px 8px',fontSize:9.5,fontWeight:700,border:'1px solid '+V.bl,background:'#fff',color:V.bl,borderRadius:3,cursor:'pointer'}}>{nome}</button>
          ))}
        </span>
      </div>

      {/* v28 · Aviso decomposição estimada */}
      {bdiAutoAviso && (
        <div style={{padding:'8px 14px',background:'#FEF3C7',borderBottom:'1px solid #F59E0B',fontSize:10.5,color:'#78350F',display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:14}}>⚠</span>
          <div style={{flex:1}}>
            <b>Decomposição estimada</b> — o MEF entrega apenas o BDI agregado ({bdiMefRef?.toFixed(2)}%). A distribuição em AC, DF, R, CT, SG, L acima foi calculada proporcionalmente ao preset MEF ANTT padrão. <b>Ajuste manualmente se necessário</b> ou consulte fonte oficial do licitante para decomposição real.
          </div>
          <button onClick={()=>setBdiAutoAviso(false)} style={{padding:'2px 8px',fontSize:10,fontWeight:700,border:'1px solid #F59E0B',background:'#fff',color:'#78350F',borderRadius:3,cursor:'pointer'}}>OK, entendi</button>
        </div>
      )}

      {/* v28 · Card de referência: BDI oficial do MEF lado a lado */}
      {bdiMefRef && (
        <div style={{padding:'8px 14px',background:'#F0FDF4',borderBottom:'1px solid '+V.gn,fontSize:10.5,color:V.br,display:'flex',alignItems:'center',gap:14}}>
          <span style={{fontSize:9,fontWeight:700,color:V.gn,letterSpacing:.5,textTransform:'uppercase'}}>📎 BDI agregado oficial (MEF)</span>
          <span style={{fontFamily:'ui-monospace,monospace',fontWeight:800,color:V.gn,fontSize:13}}>{bdiMefRef.toFixed(2)}%</span>
          <span style={{color:V.gr,fontStyle:'italic',fontSize:9.5}}>• fonte: mef_combinado.bdi.construcao_rodoviaria do JSON Lote SharePoint</span>
          <span style={{marginLeft:'auto',color:V.gr,fontSize:9.5}}>BDI calculado pelos componentes ↓ <b style={{color:bdiCalculado!=null && Math.abs((bdiCalculado*100)-bdiMefRef)<0.5 ? V.gn : V.or}}>{bdiCalculado!=null?(bdiCalculado*100).toFixed(2)+'%':'—'}</b></span>
        </div>
      )}

      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
        <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
          <th style={{...THl,width:60}}>Sigla</th>
          <th style={THl}>Componente</th>
          <th style={{...TH,width:90,background:COL_QTD}}>Meses</th>
          <th style={{...TH,width:90}}>Valor (%)</th>
          <th style={{...TH,width:130,background:COL_BASE}}>R$ aplicado</th>
          <th style={{...THl,width:90}}>Categoria</th>
        </tr></thead>
        <tbody>
          {[
            {k:'AC',     l:'Administração Central',          cat:'Direto',  pad:'3–8',  temporal:false},
            {k:'DF',     l:'Despesas Financeiras',           cat:'Direto',  pad:'1–2',  temporal:true},
            {k:'R',      l:'Risco',                          cat:'Direto',  pad:'1–2',  temporal:false},
            {k:'CT',     l:'Contingência',                   cat:'Direto',  pad:'0–2',  temporal:false},
            {k:'SG',     l:'Seguros e Garantias',            cat:'Direto',  pad:'0,2–1',temporal:true},
            {k:'L',      l:'Lucro',                          cat:'Direto',  pad:'6–12', temporal:false},
            {k:'PIS',    l:'PIS · Tributo federal',          cat:'Tributo', pad:'0,65', temporal:false},
            {k:'COFINS', l:'COFINS · Tributo federal',       cat:'Tributo', pad:'3',    temporal:false},
            {k:'ISS',    l:'ISS · Tributo municipal',        cat:'Tributo', pad:'2–5',  temporal:false},
            {k:'CPRB',   l:'CPRB · Contrib. Prev. R. Bruta', cat:'Tributo', pad:'0–4,5',temporal:false},
          ].map(c => {
            const pct = bdiComp[c.k]||0;
            const aplicado = pct/100 * custoDiretoEdital;
            return (<tr key={c.k} style={S.trLine}>
              <td style={{...TDl,fontFamily:'ui-monospace,monospace',fontWeight:800,color:c.cat==='Tributo'?V.gn:V.bl}}>{c.k}</td>
              <td style={TDl}>{c.l}</td>
              <td style={{...TD,padding:'3px 8px',background:COL_QTD}}>
                {c.temporal ? (
                  <input type="number" value={bdiMeses[c.k]||''} onChange={e=>setBdiMeses(m=>({...m,[c.k]:parseFloat(e.target.value)||0}))} placeholder="meses" style={{...inputN,width:'90%'}}/>
                ) : <span style={{color:V.gr,fontSize:9.5}}>—</span>}
              </td>
              <td style={{...TD,padding:'3px 8px'}}>
                <input type="number" step="0.01" value={pct||''} onChange={e=>setBdiComp(b=>({...b,[c.k]:parseFloat(e.target.value)||0}))} placeholder={c.pad} style={{...inputN,width:'90%'}}/>
              </td>
              <td style={{...TD,background:COL_BASE,fontFamily:'monospace',color:V.gr,fontSize:10}}>{aplicado?fmtBRL(aplicado,0):'—'}</td>
              <td style={{...TDl,fontSize:9.5,color:c.cat==='Tributo'?V.gn:V.gr}}>{c.cat}</td>
            </tr>);
          })}
        </tbody>
        <tfoot>
          <tr style={{borderTop:'2px solid '+V.b1,background:V.g0}}>
            <td style={{...TDl,fontWeight:800,color:V.br,fontFamily:'ui-monospace,monospace'}}>T</td>
            <td style={{...TDl,fontWeight:700,color:V.br}}>Tributos somados (PIS + COFINS + ISS + CPRB)</td>
            <td style={TD}></td>
            <td style={{...TD,fontWeight:800,color:V.gn,fontFamily:'ui-monospace,monospace'}}>{((bdiComp.PIS||0)+(bdiComp.COFINS||0)+(bdiComp.ISS||0)+(bdiComp.CPRB||0)).toFixed(2)}%</td>
            <td style={{...TD,background:COL_BASE,color:V.gr,fontStyle:'italic'}}>—</td>
            <td style={TDl}></td>
          </tr>
          <tr style={{borderTop:'3px solid '+V.or,background:'#FDF0E8'}}>
            <td colSpan={3} style={{...TDl,fontWeight:800,color:V.or,fontSize:12}}>BDI calculado · TCU 2622/2013</td>
            <td style={{...TD,fontWeight:800,color:V.or,fontSize:14,fontFamily:'ui-monospace,monospace'}}>{bdiCalculado!=null ? (bdiCalculado*100).toFixed(2)+'%' : '— (T ≥ 100%)'}</td>
            <td style={{...TD,fontWeight:800,color:V.or,fontFamily:'monospace',fontSize:11.5,background:COL_BASE}}>{bdiCalculado!=null ? fmtBRL(bdiCalculado * custoDiretoEdital, 0) : '—'}</td>
            <td style={{...TDl,color:V.gr,fontStyle:'italic',fontSize:9.5}}>aplica sobre Custo Direto</td>
          </tr>
        </tfoot>
      </table>

      {/* v28.2 · TOTAL GERAL · Edital + Manta Revisado + Δ explícito (3 colunas) */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',padding:'18px',background:'linear-gradient(180deg,#fff,'+V.g0+')',borderTop:'4px solid '+V.or,gap:24}}>
        <div>
          <div style={{fontSize:10,fontWeight:700,color:V.gr,textTransform:'uppercase',letterSpacing:.4,marginBottom:6}}>Total Geral · Edital</div>
          <div style={{fontSize:22,fontWeight:800,color:V.br,fontFamily:'monospace',lineHeight:1}}>{fmtBRL(totalGeralEdital,0)}</div>
          <div style={{fontSize:9.5,color:V.gr,marginTop:4}}>= {fmtBRL(custoDiretoEdital,0)} × (1 + {bdiCalculado!=null?(bdiCalculado*100).toFixed(2):'0'}%)</div>
        </div>
        <div>
          <div style={{fontSize:10,fontWeight:700,color:V.gr,textTransform:'uppercase',letterSpacing:.4,marginBottom:6}}>Total Geral · Manta Revisado</div>
          <div style={{fontSize:22,fontWeight:800,color:corDelta(totalGeralManta,totalGeralEdital),fontFamily:'monospace',lineHeight:1}}>{fmtBRL(totalGeralManta,0)}</div>
          <div style={{fontSize:9.5,color:V.gr,marginTop:4}}>= {fmtBRL(custoDiretoManta,0)} × (1 + BDI)</div>
        </div>
        <div style={{padding:'10px 14px',borderRadius:6,background:deltaCellBg(totalGeralManta,totalGeralEdital),border:'2px solid '+deltaColor(totalGeralManta,totalGeralEdital)}}>
          <div style={{fontSize:10,fontWeight:700,color:'#78350F',textTransform:'uppercase',letterSpacing:.4,marginBottom:6}}>Δ Manta vs Edital</div>
          <div style={{fontSize:22,fontWeight:800,color:deltaColor(totalGeralManta,totalGeralEdital),fontFamily:'monospace',lineHeight:1}}>{fmtDeltaRS(totalGeralManta,totalGeralEdital)}</div>
          <div style={{fontSize:14,fontWeight:700,color:deltaColor(totalGeralManta,totalGeralEdital),fontFamily:'monospace',marginTop:4}}>{fmtDeltaPct(totalGeralManta,totalGeralEdital)}</div>
          <div style={{fontSize:9,color:V.gr,marginTop:6,fontStyle:'italic'}}>{totalGeralManta < totalGeralEdital ? '↓ economia' : (totalGeralManta > totalGeralEdital ? '↑ aumento' : '= igual')}</div>
        </div>
      </div>
    </div>

    {/* ═══ QUADRO 3 · TOP-10 SERVIÇOS SICRO ═══ */}
    <div style={{...S.card,overflow:'auto',marginBottom:18}}>
      {HQ(3, 'TOP-10 da Curva A · Serviços SICRO', 'edição direta de Qtd e CU · Δ propaga para o Custo Direto')}
      <div style={{padding:'8px 14px',background:V.g0,borderBottom:'1px solid '+V.b1,fontSize:10,color:V.gr,display:'flex',gap:8,alignItems:'center'}}>
        <span style={{...S.pillBase,background:corClasse('A'),color:'#fff',fontSize:9,padding:'2px 8px'}}>● A</span>
        <span>Itens SICRO Classe A · ajustes geram Δ que soma ao Custo Direto</span>
        <button onClick={()=>setAbcServOv({})} style={{marginLeft:'auto',padding:'3px 10px',fontSize:10,fontWeight:700,border:'1px solid '+V.gr,background:'#fff',color:V.gr,borderRadius:3,cursor:'pointer'}}>↺ Resetar Q3</button>
      </div>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
        <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
          <th style={{...THl,width:30}}>#</th>
          <th style={{...THl,width:80}}>Cód SICRO</th>
          <th style={THl}>Descrição</th>
          <th style={{...TH,width:50}}>Und</th>
          <th style={{...TH,width:100,background:COL_BASE}}>Qtd Edital</th>
          <th style={{...TH,width:100,background:COL_AJ,color:V.or}}>Qtd Rev</th>
          <th style={{...TH,width:90,background:COL_BASE}}>CU Edital</th>
          <th style={{...TH,width:90,background:COL_AJ,color:V.or}}>CU Rev</th>
          <th style={{...TH,width:100,background:COL_BASE,color:V.bl}}>Total Edital</th>
          <th style={{...TH,width:110,background:COL_REV,color:V.gn}}>Total Rev</th>
          <th style={{...TH,width:100,background:COL_DELTA,color:'#78350F'}}>Δ R$</th>
          <th style={{...TH,width:65,background:COL_DELTA,color:'#78350F'}}>Δ %</th>
          <th style={{...TH,width:90,background:COL_ACUM}}>R$ acum.</th>
          <th style={{...TH,width:55,background:COL_ACUM}}>%</th>
        </tr></thead>
        <tbody>
          {top10ABCServ.length === 0 && (<tr><td colSpan={14} style={{padding:'18px',textAlign:'center',color:V.gr,fontStyle:'italic'}}>Nenhum item Classe A</td></tr>)}
          {top10ABCServ.map((it,i) => (
            <tr key={it.c} style={S.trLine}>
              <td style={{...TD,fontWeight:700,color:V.gr}}>{i+1}</td>
              <td style={{...TDl,fontFamily:'ui-monospace,monospace',fontWeight:700,color:V.bl,fontSize:10}}>{it.c}</td>
              <td style={{...TDl,fontSize:10}}>{it.desc.length>55 ? it.desc.slice(0,55)+'…' : it.desc}</td>
              <td style={{...TD,fontSize:9.5,color:V.gr}}>{it.und}</td>
              <td style={{...TD,fontFamily:'monospace',background:COL_BASE,color:V.gr,fontSize:10}}>{fmtNum(it.qtd,0)}</td>
              <td style={{...TD,padding:'3px 6px',background:COL_AJ}}>
                <input type="number" step="0.01" value={abcServOv[it.c]?.qtd ?? ''} onChange={e=>{const v=e.target.value===''?null:(parseFloat(e.target.value)||0); setAbcServOv(o=>({...o,[it.c]:{...o[it.c],qtd:v}}));}} placeholder={fmtNum(it.qtd,0)} style={{...inputN,width:'100%',fontSize:10}}/>
              </td>
              <td style={{...TD,fontFamily:'monospace',background:COL_BASE,color:V.gr,fontSize:10}}>{fmtBRL(it.cu,2)}</td>
              <td style={{...TD,padding:'3px 6px',background:COL_AJ}}>
                <input type="number" step="0.01" value={abcServOv[it.c]?.cu ?? ''} onChange={e=>{const v=e.target.value===''?null:(parseFloat(e.target.value)||0); setAbcServOv(o=>({...o,[it.c]:{...o[it.c],cu:v}}));}} placeholder={fmtBRL(it.cu,0).replace('R$','').trim()} style={{...inputN,width:'100%',fontSize:10}}/>
              </td>
              <td style={{...TD,fontFamily:'monospace',background:COL_BASE,color:V.gr,fontSize:10}}>{fmtBRL(it.total,0)}</td>
              <td style={{...TD,fontWeight:700,color:corDelta(it.totalRev,it.total),background:COL_REV,fontSize:10}}>{fmtBRL(it.totalRev,0)}</td>
              <td style={{...TD,fontWeight:700,fontSize:10,fontFamily:'monospace',color:deltaColor(it.totalRev,it.total),background:deltaCellBg(it.totalRev,it.total)}}>{fmtDeltaRS(it.totalRev,it.total)}</td>
              <td style={{...TD,fontWeight:700,fontSize:10,fontFamily:'monospace',color:deltaColor(it.totalRev,it.total),background:deltaCellBg(it.totalRev,it.total)}}>{fmtDeltaPct(it.totalRev,it.total)}</td>
              <td style={{...TD,background:COL_ACUM,fontFamily:'monospace',fontSize:10}}>{fmtBRL(it.acumRev,0)}</td>
              <td style={{...TD,background:COL_ACUM,fontWeight:700,color:V.bl,fontSize:10}}>{it.pctAcumRev.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{borderTop:'3px solid '+V.br,background:V.g0}}>
            <td colSpan={8} style={{...TDl,fontWeight:800,color:V.br,fontSize:11,padding:'10px 12px'}}>TOTAL TOP-10 Classe A · Edital × Manta</td>
            <td style={{...TD,fontWeight:800,color:V.br,fontSize:11,background:COL_BASE,padding:'10px 12px',fontFamily:'monospace'}}>{fmtBRL(top10ServBase,0)}</td>
            <td style={{...TD,fontWeight:800,color:corDelta(top10ServRev,top10ServBase),fontSize:11,background:COL_REV,padding:'10px 12px',fontFamily:'monospace'}}>{fmtBRL(top10ServRev,0)}</td>
            <td style={{...TD,fontWeight:800,fontSize:11,fontFamily:'monospace',color:deltaColor(top10ServRev,top10ServBase),background:deltaCellBg(top10ServRev,top10ServBase),padding:'10px 12px'}}>{fmtDeltaRS(top10ServRev,top10ServBase)}</td>
            <td style={{...TD,fontWeight:800,fontSize:11,fontFamily:'monospace',color:deltaColor(top10ServRev,top10ServBase),background:deltaCellBg(top10ServRev,top10ServBase),padding:'10px 12px'}}>{fmtDeltaPct(top10ServRev,top10ServBase)}</td>
            <td colSpan={2} style={{...TD,padding:'10px 12px',background:COL_ACUM}}></td>
          </tr>
        </tfoot>
      </table>
    </div>

    {/* ═══ QUADRO 4 · TOP-10 INSUMOS ═══ */}
    <div style={{...S.card,overflow:'auto',marginBottom:8}}>
      {HQ(4, 'TOP-10 da Curva A · Insumos', 'placeholder · começam vazios · total propaga para Custo Direto')}
      <div style={{padding:'8px 14px',background:V.g0,borderBottom:'1px solid '+V.b1,fontSize:10,color:V.gr,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{...S.pillBase,background:corClasse('A'),color:'#fff',fontSize:9,padding:'2px 8px'}}>● A</span>
        <span>{abcInsLista.length===0 ? 'Lista vazia · adicione insumos para começar' : abcInsLista.length+' insumo(s) · TOP-10 abaixo'}</span>
        <button onClick={addInsumo} style={{padding:'3px 10px',fontSize:10,fontWeight:700,border:'1px solid '+V.bl,background:'#fff',color:V.bl,borderRadius:3,cursor:'pointer',marginLeft:'auto'}}>+ Adicionar insumo</button>
        <button onClick={importInsumosJson} style={{padding:'3px 10px',fontSize:10,fontWeight:700,border:'1px solid '+V.bl,background:'#fff',color:V.bl,borderRadius:3,cursor:'pointer'}}>📋 Colar JSON</button>
        <button onClick={prePopularInsumosTipicos} title="Adiciona 15 insumos típicos rodoviários com CU estimado de mercado (Cap 16 · fail-loud · ajuste após)" style={{padding:'3px 10px',fontSize:10,fontWeight:700,border:'1px solid #f59e0b',background:'#fffbeb',color:'#78350f',borderRadius:3,cursor:'pointer'}}>⚡ Pré-pop típicos</button>
        <button onClick={decomporInsumosDoSicro} title="Decompõe TOP-10 SICRO em insumos via regras de bolso (Cap 16 · fail-loud · valide caso a caso)" style={{padding:'3px 10px',fontSize:10,fontWeight:700,border:'1px solid #8b5cf6',background:'#f5f3ff',color:'#6d28d9',borderRadius:3,cursor:'pointer'}}>🔬 Decompor Q3</button>
      </div>

      {/* v28.1 · Diagnóstico fail-loud Q4 (Cap 16) */}
      {abcInsLista.length === 0 && (
        <div style={{padding:'10px 14px',background:V.g0,borderBottom:'1px solid '+V.b1,fontSize:10.5,color:V.gr,lineHeight:1.6}}>
          <div style={{fontWeight:700,color:V.br,marginBottom:4}}>📊 Diagnóstico Q4 (Cap 16 · fail-loud)</div>
          <div>· <b>CONSOL_FIN[{sig}]</b>: {(()=>{ const cf=CONSOL_FIN[sig]||{}; const total = Object.values(cf).reduce((s,arr)=>s+arr.length,0); return total+' itens SICRO disponíveis'; })()}</div>
          <div>· <b>Insumos cadastrados</b>: 0 (vazio por design)</div>
          <div style={{fontStyle:'italic',marginTop:4}}>Insumos começam vazios porque o PER/Edital ANTT entrega serviços agregados, não insumos do canteiro. Use os botões acima ou cadastre manualmente.</div>
        </div>
      )}
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
        <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
          <th style={{...THl,width:30}}>#</th>
          <th style={{...THl,width:30}}></th>
          <th style={THl}>Descrição</th>
          <th style={{...TH,width:60}}>Classe</th>
          <th style={{...TH,width:110,background:COL_QTD}}>Qtde</th>
          <th style={{...TH,width:55,background:COL_QTD}}>Und</th>
          <th style={{...TH,width:120,background:COL_BASE}}>CU (R$)</th>
          <th style={{...TH,width:120,background:COL_REV}}>Total (Q×CU)</th>
          <th style={{...TH,width:110,background:COL_ACUM}}>R$ acum.</th>
          <th style={{...TH,width:60,background:COL_ACUM}}>%</th>
        </tr></thead>
        <tbody>
          {top10ABCIns.length===0 && (<tr><td colSpan={10} style={{padding:'24px',textAlign:'center',color:V.gr,fontStyle:'italic'}}>
            Nenhum insumo · clique &quot;+ Adicionar insumo&quot; ou &quot;📋 Colar JSON&quot;
          </td></tr>)}
          {top10ABCIns.map((it,i) => (
            <tr key={it.id} style={S.trLine}>
              <td style={{...TD,fontWeight:700,color:V.gr}}>{i+1}</td>
              <td style={TD}>
                <button onClick={()=>removeInsumo(it.id)} title="Remover" style={{padding:'1px 7px',fontSize:11,fontWeight:700,border:'1px solid '+V.gr,background:'#fff',color:V.gr,borderRadius:3,cursor:'pointer'}}>×</button>
              </td>
              <td style={{...TDl,padding:'3px 8px'}}>
                <input type="text" value={it.desc} onChange={e=>updateInsumo(it.id,{desc:e.target.value})} style={{width:'100%',fontSize:10.5,padding:'3px 6px',border:'1px solid '+V.b1,borderRadius:3,outline:'none'}}/>
              </td>
              <td style={{...TD,padding:'3px 8px'}}>
                <select value={it.classe} onChange={e=>updateInsumo(it.id,{classe:e.target.value})} style={{width:'100%',fontSize:10.5,fontWeight:700,padding:'3px 4px',border:'1px solid '+V.b1,borderRadius:3,cursor:'pointer',color:corClasse(it.classe),outline:'none',background:'#fff'}}>
                  <option value="A">A</option><option value="B">B</option><option value="C">C</option>
                </select>
              </td>
              <td style={{...TD,padding:'3px 8px',background:COL_QTD}}>
                <input type="number" value={it.qtd||''} onChange={e=>updateInsumo(it.id,{qtd:parseFloat(e.target.value)||0})} placeholder="0" style={{...inputN,width:'100%'}}/>
              </td>
              <td style={{...TD,padding:'3px 4px',background:COL_QTD}}>
                <input type="text" value={it.und||''} onChange={e=>updateInsumo(it.id,{und:e.target.value})} placeholder="un" style={{width:'100%',fontSize:10,padding:'3px 4px',border:'1px solid '+V.b1,borderRadius:3,textAlign:'center',outline:'none'}}/>
              </td>
              <td style={{...TD,padding:'3px 8px',background:COL_BASE}}>
                <input type="number" step="0.01" value={it.cu||''} onChange={e=>updateInsumo(it.id,{cu:parseFloat(e.target.value)||0})} placeholder="R$ 0" style={{...inputN,width:'100%'}}/>
              </td>
              <td style={{...TD,fontWeight:700,color:V.br,background:COL_REV,fontFamily:'monospace'}}>{it.total?fmtBRL(it.total,0):'—'}</td>
              <td style={{...TD,background:COL_ACUM,fontFamily:'monospace',fontSize:10}}>{fmtBRL(it.acum,0)}</td>
              <td style={{...TD,background:COL_ACUM,fontWeight:700,color:V.bl}}>{it.pctAcum.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
        {top10ABCIns.length > 0 && (<tfoot>
          <tr style={{borderTop:'3px solid '+V.br,background:V.g0}}>
            <td colSpan={7} style={{...TDl,fontWeight:800,color:V.br,fontSize:11,padding:'10px 12px'}}>TOTAL TOP-10 Insumos · Δ propaga ao Custo Direto</td>
            <td style={{...TD,fontWeight:800,color:V.br,fontSize:11,background:COL_REV,padding:'10px 12px',fontFamily:'monospace'}}>{fmtBRL(top10InsBase,0)}</td>
            <td colSpan={2} style={{...TD,background:COL_ACUM,padding:'10px 12px',color:V.gr,fontStyle:'italic',fontSize:10}}>
              {abcInsLista.length > 10 ? `+${abcInsLista.length-10} fora do TOP-10 · total geral: ${fmtBRL(totalInsTodos,0)}` : 'lista completa'}
            </td>
          </tr>
        </tfoot>)}
      </table>
    </div>

    <div style={{marginTop:10,fontSize:10,color:V.gr,lineHeight:1.5,padding:'10px 14px',background:V.g0,borderRadius:6,border:'1px solid '+V.g1}}>
      <b style={{color:V.br}}>Q1 Custo Direto:</b> Σ Obras (Q×CU ou Edital + ajuste %/R$) + Σ Indiretos (meses × R$/mês + ajuste) + Δ_Q3 + Δ_Q4. Aviso ⚠ ativo quando ambas as vias estão editadas.<br/>
      <b style={{color:V.br}}>Q2 BDI + Total Geral:</b> Total Geral = Custo Direto × (1 + BDI). BDI: TCU 2622/2013 com Contingência somando ao R no fator. Meses em DF/SG são informativos.<br/>
      <b style={{color:V.br}}>Q3 Serviços SICRO:</b> TOP-10 da Classe A · Δ = Total Revisado − Total Edital → soma ao Custo Direto.<br/>
      <b style={{color:V.br}}>Q4 Insumos:</b> TOP-10 da lista cadastrada · total cadastrado vira Δ → soma ao Custo Direto (insumos começam vazios).<br/>
      <b style={{color:V.br}}>Persistência:</b> tudo no localStorage por lote · migração automática de v1–v5.
    </div>
  </div>);
});

export { OrcamentoRevisaoTab };
export default OrcamentoRevisaoTab;
